import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Loader2, Shield, WifiOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import NoindexHead from "@/components/NoindexHead";
import { APP_BUILD_VERSION } from "@/buildVersion";
import { filterRedirectSearch, sanitizeReturnRoute } from "@/lib/adminSections";
import { setCsrfToken } from "@/App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25pknzhh_ee436b9cf26fbe3a949749993a8a0799c33dc4d497d0bf4b65f923b400bf1fc0.jpeg";
const API = `${window.location.origin}/api`;

// Simple hash for offline credential verification (not for security — just a local check)
const hashCredentials = async (email, password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(`${email.toLowerCase().trim()}:${password}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  
  // Forgot password UI toggle (Phase 5 containment: recovery is disabled —
  // this only reveals a neutral message; no email/token/reset flow remains).
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Track online/offline status
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  // If a session flag is present, skip login (the httpOnly cookie carries the
  // real session; the dashboard verifies it and redirects back here on 401).
  useEffect(() => {
    const hasSession = localStorage.getItem('admin_session');
    if (hasSession) {
      navigate(`/admin/jobs${filterRedirectSearch(window.location.search)}`);
    }
  }, [navigate]);

  // Version-drift self-healer: if this (cached) login page is running an older
  // build than the server, the user is stuck on a stale PWA/service-worker
  // cache (the dashboard's own drift check can't help because login bounces
  // before reaching it). Unregister SWs, wipe caches, and hard-reload to pull
  // the latest build automatically.
  useEffect(() => {
    let cancelled = false;
    const checkVersion = async () => {
      try {
        const res = await fetch(`${API}/app/version`, { cache: 'no-store' });
        if (!res.ok) return;
        const { version: serverVersion } = await res.json();
        if (cancelled || !serverVersion || serverVersion === APP_BUILD_VERSION) return;
        // eslint-disable-next-line no-console
        console.warn(`[SHS] Login version drift: client=${APP_BUILD_VERSION} server=${serverVersion} — refreshing`);
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        setTimeout(() => window.location.reload(), 800);
      } catch (e) { /* offline / endpoint missing — ignore */ }
    };
    checkVersion();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/login`, loginData);
      // The server set a secure httpOnly session cookie. We only persist a
      // NON-sensitive session flag (never the token) so the SPA knows a session
      // exists, plus a credential hash for offline (PWA) re-entry.
      localStorage.setItem("admin_session", "1");
      
      // Cache credential hash for offline login
      const credHash = await hashCredentials(loginData.email, loginData.password);
      localStorage.setItem("offline_cred_hash", credHash);
      localStorage.setItem("offline_admin_email", loginData.email.toLowerCase().trim());
      
      toast.success("Login successful!");
      // Phase 5: fetch a session-bound CSRF token (harmless while enforcement off).
      try {
        const csrf = await axios.get(`${API}/admin/csrf-token`);
        if (csrf.data?.csrf_token) setCsrfToken(csrf.data.csrf_token);
      } catch (e) { /* csrf optional until enforcement enabled */ }
      // Phase 5: safe return to the originally-requested admin route.
      let dest = `/admin/jobs${filterRedirectSearch(window.location.search)}`;
      try {
        const saved = sessionStorage.getItem('admin_return_to');
        sessionStorage.removeItem('admin_return_to');
        if (saved) dest = sanitizeReturnRoute(saved);
      } catch (e) { /* default applies */ }
      navigate(dest);
    } catch (error) {
      // If network error (offline), try cached credentials
      if (!navigator.onLine || error.code === 'ERR_NETWORK' || !error.response) {
        try {
          const credHash = await hashCredentials(loginData.email, loginData.password);
          const cachedHash = localStorage.getItem("offline_cred_hash");
          const cachedEmail = localStorage.getItem("offline_admin_email");
          const cachedSession = localStorage.getItem("admin_session");

          if (cachedHash && credHash === cachedHash &&
              cachedEmail === loginData.email.toLowerCase().trim() && cachedSession) {
            toast.success("Signed in offline (cached session)");
            navigate(`/admin/jobs${filterRedirectSearch(window.location.search)}`);
            return;
          } else if (cachedHash && credHash !== cachedHash) {
            toast.error("Incorrect credentials. Offline login requires matching your last successful login.");
          } else {
            // Iter 73: be honest about what actually happened. Previously
            // we showed "No cached session" which sounded like the SW was
            // at fault — but the real cause is the POST itself failing.
            // The "Reset cache & reload" button below is the recovery
            // path Sandy should reach for if a stale SW is involved.
            const reason = !navigator.onLine
              ? "you appear to be offline"
              : (error.code === 'ERR_NETWORK' ? "network blocked the request (CORS / extension / VPN)" : "no response from server");
            toast.error(`Couldn't reach the login server — ${reason}. If this persists, tap "Reset cache & reload" below.`);
          }
        } catch (offlineErr) {
          toast.error("Offline login failed. Please connect to the internet.");
        }
      } else {
        console.error("Login error:", error);
        toast.error(error.response?.data?.detail || "Invalid credentials");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Iter 73: self-service "stale service worker" recovery.  If the user is
  // hitting the login flow via a PWA window that's still running an older
  // SW build, this hard-purges every SW + cache + localStorage flag and
  // then reloads, guaranteeing the next request goes straight to the
  // network with the latest bundle.
  const handleResetCache = async () => {
    if (!window.confirm("Wipe local app cache and reload?\n\nThis won't delete any business data — it only clears the offline cache so the latest version of the app is fetched.")) {
      return;
    }
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Keep email so they don't have to retype it, drop everything else.
      const email = localStorage.getItem('admin_email');
      localStorage.clear();
      if (email) localStorage.setItem('admin_email', email);
    } catch (err) {
      console.warn('Cache reset failed:', err);
    } finally {
      // Force reload bypassing any remaining HTTP cache.
      window.location.reload();
    }
  };

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <NoindexHead title="Admin Sign-in | Sustainable Handyman Services" />
      {/* Offline Banner */}
      {isOffline && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff className="h-4 w-4" />
          You're offline — sign in with your last used credentials
        </div>
      )}
      {/* Header Background */}
      <div className="h-[30vh] bg-primary relative">
        <div className="absolute top-4 left-4">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-link">
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </Link>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="bg-white/10 p-4 rounded-full inline-block mb-3">
              <Shield className="h-10 w-10" />
            </div>
            <h1 className="text-2xl font-bold font-heading">Admin Portal</h1>
          </div>
        </div>
      </div>

      {/* Login/Forgot Password Card */}
      <div className="max-w-md mx-auto px-4 -mt-[10vh] pb-12">
        <Card className="shadow-lg" data-testid="admin-auth-card">
          <CardHeader className="text-center">
            <img src={LOGO_URL} alt="Logo" className="h-14 w-14 rounded-full mx-auto mb-2 object-cover" />
            <CardTitle className="font-heading">
              {showForgotPassword ? "Password Recovery" : "Welcome Back"}
            </CardTitle>
            <CardDescription>
              {showForgotPassword ? "Recovery is currently unavailable" : "Sign in to manage enquiries"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showForgotPassword ? (
              // Login Form
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="admin@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                    className="h-12"
                    data-testid="login-email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                    className="h-12"
                    data-testid="login-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-lg font-medium"
                  disabled={isLoading}
                  data-testid="login-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
                <div className="text-center pt-2 border-t border-border/50">
                  <button
                    type="button"
                    onClick={handleResetCache}
                    className="text-xs text-muted-foreground hover:text-destructive hover:underline"
                    data-testid="reset-pwa-cache"
                    title="Clears the local app cache and reloads with the latest version. Use only if sign-in keeps failing."
                  >
                    Reset cache &amp; reload
                  </button>
                </div>
              </form>
            ) : (
              // Phase 5 containment: no email/token/reset flow. Neutral message
              // only — directs the admin to the authorised manual procedure.
              <div className="text-center space-y-4" data-testid="recovery-unavailable-message">
                <div className="bg-muted p-4 rounded-full inline-block">
                  <Shield className="h-10 w-10 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Self-service password recovery is currently unavailable. To
                  regain access, please contact an authorised administrator to
                  use the manual account-recovery procedure.
                </p>
                <Button
                  onClick={resetForgotPasswordFlow}
                  className="w-full rounded-full bg-primary hover:bg-primary/90 h-12"
                  data-testid="back-to-login"
                >
                  Back to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
